import Swiper from 'swiper';
import {gsap} from '../../../vendors/gsap';
import {Draggable} from '../../../vendors/gsap/Draggable';
import createStoryCard from '../createStoryCard/index';
import createArrowBtn from '../../common/createArrowBtn/index';
import getDOMfromString from '../../../helpers/getDOMfromString';
import updateCarouselTabIndex from '../../../helpers/updateCarouselTabIndex';
import classes from './styles.module.css';

/**
 * This method creates and return carousel
 *
 * @param {Object} config - options to customize carousel
 * @returns {Object} - next/prev slide click handlers
 */
const createRelatedCarousel = config => {
    const {el, storyData, labelPrevious, labelNext} = config ?? {};
    const data = Array.isArray(storyData) ? storyData : [];
    let slides = [];
    let container = null;
    let relatedSwiper = null;

    /**
     * Handles the drag functionality
     *
     * @param {Object} dragConfig - trigger element, navigation click handlers
     * @returns {Object} - draggable object
     */
    function dragHandler(dragConfig) {
        const {triggerEl = container, slideNext, slidePrev} = dragConfig;
        gsap.registerPlugin(Draggable);
        const proxy = document.createElement('div');
        return Draggable.create(proxy, {
            type: 'x',
            lockAxis: true,
            trigger: triggerEl,
            /**
             * Executed on drag start
             */
            onDragStart() {
                if (!slideNext) return;

                if (this.getDirection() === 'left') {
                    slideNext();
                } else {
                    slidePrev();
                }
            }
        });
    }

    /**
     * This function will generate the html for carousel
     */
    function generateCarousel() {
        const sliderStr = `<div class="related-story-carousel swiper ${classes.relatedStoryCarousel}" id="related-story-carousel">
        <div class="swiper-wrapper ${classes.swiperWrapper}"></div>
    </div>`;
        const btnStr = `<div class="related-navigation ${classes.carouselNavigation}">${createArrowBtn({
            type: 'left',
            label: labelPrevious,
            customClasses: ['prev', 'related-nav-prev', classes.prev],
            htmlStr: true
        })}
        ${createArrowBtn({
            type: 'right',
            label: labelNext,
            customClasses: ['next', 'related-nav-next', classes.next],
            htmlStr: true
        })}</div>`;
        container = getDOMfromString(sliderStr);
        const wrapper = container.querySelector('.swiper-wrapper');

        slides = getSlides();
        wrapper.append(...slides);
        el.append(container, getDOMfromString(btnStr));
    }

    /**
     * This function generates and returns new slides
     *
     * @returns {HTMLElement} - slides
     */
    function getSlides() {
        const newSlides = [];
        data.forEach(item => {
            let imageUrl = new URL(item.tileImage.src);
            const scaleWidth = 400;
            const scaleFactor = scaleWidth / item.tileImage.width;
            const scaleHeight = scaleFactor & item.tileImage.height;

            // Related are really small images
            imageUrl.searchParams.set('sw', scaleWidth);

            const title = item.shortTitle || item.name;
            const src = imageUrl;
            const slide = getDOMfromString(
                `<div class="swiper-slide ${classes.swiperSlide}" aria-hidden="true">${createStoryCard({
                    src,
                    alt: item.tileImage.alt || item.name,
                    width: scaleWidth,
                    height: scaleHeight,
                    title,
                    id: item.id
                })}</div>`
            );
            newSlides.push(slide);
        });

        return newSlides;
    }

    /**
     * Initialize and returns swiper object
     *
     * @returns {Object} swiper - carousel object
     */
    function initializeSwiper() {
        relatedSwiper = new Swiper('#related-story-carousel', {
            lazy: true,
            loop: true,
            spaceBetween: 12,
            slidesPerView: 'auto',
            allowTouchMove: false,
            speed: 700,
            watchOverflow: true,
            watchSlidesProgress: true,
            on: {
                /**
                 * @param {Swiper} swiper - swiper instance
                 */
                afterInit: swiper => {
                    setTimeout(() => updateCarouselTabIndex(swiper, '.swiper-slide-visible', false), 700);
                },
                /**
                 * @param {Swiper} swiper - swiper instance
                 */
                slideChangeTransitionEnd: swiper => {
                    updateCarouselTabIndex(swiper, '.swiper-slide-visible', false);
                }
            }
        });

        return relatedSwiper;
    }

    // generating the carousel
    generateCarousel();

    // initializing the swiper
    relatedSwiper = initializeSwiper();

    return {
        relatedSwiper,
        relatedDragHandler: dragHandler,
        getSlides
    };
};

export default createRelatedCarousel;
